class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector("form");
    facetForm.addEventListener("input", this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector("#FacetsWrapperDesktop");
    if (facetWrapper) facetWrapper.addEventListener("keyup", onKeyUpEscape);

    // Fix for mobile facet drawer close button aria-expanded attribute
    document.addEventListener("click", (event) => {
      if (
        event.target.closest(
          ".mobile-facets__close, .mobile-facets__close-button"
        )
      ) {
        const mobileDetails = event.target.closest("details");
        if (
          mobileDetails &&
          mobileDetails.querySelector(".mobile-facets__close-button")
        ) {
          mobileDetails
            .querySelector(".mobile-facets__close-button")
            .setAttribute("aria-expanded", "false");
        }
      }
    });
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state
        ? event.state.searchParams
        : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener("popstate", onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll(".js-facet-remove").forEach((element) => {
      element.classList.toggle("disabled", disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById("ProductCount");
    const countContainerDesktop = document.getElementById(
      "ProductCountDesktop"
    );
    const loadingSpinners = document.querySelectorAll(
      ".facets-container .loading__spinner, facet-filters-form .loading__spinner"
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove("hidden"));
    document
      .getElementById("ProductGridContainer")
      .querySelector(".collection")
      .classList.add("loading");
    if (countContainer) {
      countContainer.classList.add("loading");
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add("loading");
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);

    // Dispatch an event to notify that the facet form has been updated
    document.dispatchEvent(new CustomEvent("facetFilterFormUpdated"));
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [
          ...FacetFiltersForm.filterData,
          { html, url },
        ];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === "function")
          initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === "function")
      initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById("ProductGridContainer").innerHTML = new DOMParser()
      .parseFromString(html, "text/html")
      .getElementById("ProductGridContainer").innerHTML;

    document
      .getElementById("ProductGridContainer")
      .querySelectorAll(".scroll-trigger")
      .forEach((element) => {
        element.classList.add("scroll-trigger--cancel");
      });
  }

  static renderProductCount(html) {
    const count = new DOMParser()
      .parseFromString(html, "text/html")
      .getElementById("ProductCount").innerHTML;
    const container = document.getElementById("ProductCount");
    const containerDesktop = document.getElementById("ProductCountDesktop");
    container.innerHTML = count;
    container.classList.remove("loading");
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove("loading");
    }
    const loadingSpinners = document.querySelectorAll(
      ".facets-container .loading__spinner, facet-filters-form .loading__spinner"
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add("hidden"));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, "text/html");
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      "#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter"
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      "#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter"
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (
        !Array.from(facetDetailsElementsFromFetch).some(
          ({ id }) => currentElement.id === id
        )
      ) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest(".js-filter") : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter(
      (element) => !matchesId(element)
    );
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(
      matchesId
    );

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML =
          elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } =
            facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          console.log(elementToRender);
          if (elementToRender.parentElement.id) {
            document
              .querySelector(`#${elementToRender.parentElement.id} .js-filter`)
              .before(elementToRender);
          }
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest(".js-filter").id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(
          countsToRender,
          event.target.closest(".js-filter")
        );
        FacetFiltersForm.renderMobileCounts(
          countsToRender,
          document.getElementById(closestJSFilterID)
        );

        const newFacetDetailsElement =
          document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains(
          "mobile-facets__details"
        )
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate =
          newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute("type") === "text";

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = [
      ".active-facets-mobile",
      ".active-facets-desktop",
    ];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML =
        activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = [
      ".mobile-facets__open",
      ".mobile-facets__count",
      ".sorting",
    ];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML =
        html.querySelector(selector).innerHTML;
    });

    document
      .getElementById("FacetFiltersFormMobile")
      .closest("menu-drawer")
      .bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector(".facets__summary");
    const sourceSummary = source.querySelector(".facets__summary");

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector(".facets__header");
    const sourceHeaderElement = source.querySelector(".facets__header");

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector(".facets-wrap");
    const sourceWrapElement = source.querySelector(".facets-wrap");

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(
        target.querySelector("show-more-button .label-show-more.hidden")
      );
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll(".facets__item.hidden")
          .forEach((hiddenItem) =>
            hiddenItem.classList.replace("hidden", "show-more-item")
          );
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector(".mobile-facets__list");
    const sourceFacetsList = source.querySelector(".mobile-facets__list");

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState(
      { searchParams },
      "",
      `${window.location.pathname}${searchParams && "?".concat(searchParams)}`
    );
  }

  static getSections() {
    return [
      {
        section: document.getElementById("product-grid").dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const targetForm = event.target.closest("form");
    const isSortForm =
      targetForm.id === "FacetSortForm" ||
      targetForm.id === "FacetSortDrawerForm";
    const isFilterForm =
      targetForm.id === "FacetFiltersForm" ||
      targetForm.id === "FacetFiltersFormMobile";

    if (isFilterForm) {
      const searchParams = this.createSearchParams(targetForm);
      this.onSubmitForm(searchParams, event);
    } else if (isSortForm) {
      const sortParams = this.createSearchParams(targetForm);
      const currentParams = new URLSearchParams(window.location.search);
      currentParams.set(
        "sort_by",
        new URLSearchParams(sortParams).get("sort_by")
      );
      this.onSubmitForm(currentParams.toString(), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf("?") == -1
        ? ""
        : event.currentTarget.href.slice(
            event.currentTarget.href.indexOf("?") + 1
          );
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define("facet-filters-form", FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll("input").forEach((element) => {
      element.addEventListener("change", this.onRangeChange.bind(this));
      element.addEventListener("keydown", this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern =
      /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll("input");
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute("data-max", maxInput.value);
    if (minInput.value) maxInput.setAttribute("data-min", minInput.value);
    if (minInput.value === "") maxInput.setAttribute("data-min", 0);
    if (maxInput.value === "")
      minInput.setAttribute("data-max", maxInput.getAttribute("data-max"));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute("data-min"));
    const max = Number(input.getAttribute("data-max"));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define("price-range", PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector("a");
    facetLink.setAttribute("role", "button");
    facetLink.addEventListener("click", this.closeFilter.bind(this));
    facetLink.addEventListener("keyup", (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === "SPACE") this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form =
      this.closest("facet-filters-form") ||
      document.querySelector("facet-filters-form");
    form.onActiveFilterClick(event);
  }
}

customElements.define("facet-remove", FacetRemove);

// Horizontal filters functionality
document.addEventListener("DOMContentLoaded", () => {
  setupHorizontalFilters();
});

function setupHorizontalFilters() {
  // Create a custom form for horizontal filters to submit independently
  const horizontalFiltersWrapper = document.querySelector(
    ".horizontal-filters-wrapper"
  );
  if (horizontalFiltersWrapper) {
    // Create a hidden form for the horizontal filters
    const horizontalForm = document.createElement("form");
    horizontalForm.id = "HorizontalFiltersForm";
    horizontalForm.style.display = "none";
    horizontalFiltersWrapper.appendChild(horizontalForm);

    // Clone initial filter state from mobile filters
    syncFormFromMobile();

    // Handle Price Range filter sync
    const horizontalPriceInputs = document.querySelectorAll(
      '.horizontal-price-range input[type="text"]'
    );
    if (horizontalPriceInputs.length) {
      horizontalPriceInputs.forEach((input) => {
        input.addEventListener("change", function () {
          // Find the corresponding input in the mobile filter drawer
          const inputId = this.id;
          const paramName = inputId.includes("GTE")
            ? "filter.v.price.gte"
            : "filter.v.price.lte";
          const drawerInputSelector = inputId.includes("GTE")
            ? "#Mobile-Filter-Price-GTE"
            : "#Mobile-Filter-Price-LTE";

          const drawerInput = document.querySelector(drawerInputSelector);
          if (drawerInput) {
            // Set the value in the main filter
            drawerInput.value = this.value;

            // Add/update input in the horizontal form
            updateHorizontalFormInput(paramName, this.value);

            // Submit the form
            submitHorizontalFilterForm();
          }
        });
      });
    }

    // Handle Brand/Vendor filter sync
    const horizontalBrandCheckboxes = document.querySelectorAll(
      '.horizontal-filter[data-filter-type="brand"] input[type="checkbox"]'
    );
    if (horizontalBrandCheckboxes.length) {
      horizontalBrandCheckboxes.forEach((checkbox) => {
        checkbox.addEventListener("change", function () {
          // Find the corresponding checkbox in the drawer filter
          const value = this.getAttribute("data-value");
          const name = this.getAttribute("name");

          // Try to find the matching checkbox in the mobile filter
          const drawerCheckboxSelector = `#FacetFiltersFormMobile input[name="${name}"][value="${value}"]`;
          const drawerCheckbox = document.querySelector(drawerCheckboxSelector);

          if (drawerCheckbox) {
            // Update the checkbox in the drawer to match the horizontal filter
            drawerCheckbox.checked = this.checked;

            // Add/remove from horizontal form
            if (this.checked) {
              updateHorizontalFormInput(name, value);
            } else {
              removeHorizontalFormInput(name, value);
            }

            // Submit the form
            submitHorizontalFilterForm();
          }
        });
      });
    }

    // Handle reset buttons in the horizontal filters
    const resetButtons = document.querySelectorAll(
      ".horizontal-filter .facets__reset"
    );
    if (resetButtons.length) {
      resetButtons.forEach((button) => {
        button.addEventListener("click", function (event) {
          event.preventDefault();
          const filterType =
            this.closest(".horizontal-filter").getAttribute("data-filter-type");

          if (filterType === "price") {
            // Reset price inputs in both horizontal and drawer filters
            const minPriceInput = document.querySelector(
              "#Horizontal-Filter-Price-GTE"
            );
            const maxPriceInput = document.querySelector(
              "#Horizontal-Filter-Price-LTE"
            );
            const drawerMinPriceInput = document.querySelector(
              "#Mobile-Filter-Price-GTE"
            );
            const drawerMaxPriceInput = document.querySelector(
              "#Mobile-Filter-Price-LTE"
            );

            if (minPriceInput) minPriceInput.value = "";
            if (maxPriceInput) maxPriceInput.value = "";
            if (drawerMinPriceInput) drawerMinPriceInput.value = "";
            if (drawerMaxPriceInput) drawerMaxPriceInput.value = "";

            // Remove price inputs from horizontal form
            removeHorizontalFormInput("filter.v.price.gte");
            removeHorizontalFormInput("filter.v.price.lte");
          } else if (filterType === "brand") {
            // Reset all brand checkboxes in both horizontal and drawer filters
            const brandCheckboxes = document.querySelectorAll(
              '.horizontal-filter[data-filter-type="brand"] input[type="checkbox"]'
            );
            const drawerBrandCheckboxes = document.querySelectorAll(
              '#FacetFiltersFormMobile input[name^="filter.p.vendor"]'
            );

            brandCheckboxes.forEach((checkbox) => {
              checkbox.checked = false;
              removeHorizontalFormInput(
                checkbox.getAttribute("name"),
                checkbox.getAttribute("data-value")
              );
            });

            drawerBrandCheckboxes.forEach((checkbox) => {
              checkbox.checked = false;
            });
          }

          // Submit the form to apply reset
          submitHorizontalFilterForm();
        });
      });
    }
  }

  // Function to sync mobile form state to horizontal form on page load
  function syncFormFromMobile() {
    const horizontalForm = document.getElementById("HorizontalFiltersForm");
    const mobileForm = document.getElementById("FacetFiltersFormMobile");

    if (horizontalForm && mobileForm) {
      // Clear horizontal form
      horizontalForm.innerHTML = "";

      // Copy price filter values
      const drawerPriceMin = document.querySelector("#Mobile-Filter-Price-GTE");
      const drawerPriceMax = document.querySelector("#Mobile-Filter-Price-LTE");

      if (drawerPriceMin && drawerPriceMin.value) {
        updateHorizontalFormInput("filter.v.price.gte", drawerPriceMin.value);
      }

      if (drawerPriceMax && drawerPriceMax.value) {
        updateHorizontalFormInput("filter.v.price.lte", drawerPriceMax.value);
      }

      // Copy checked brand checkboxes
      const checkedBrandCheckboxes = mobileForm.querySelectorAll(
        'input[type="checkbox"]:checked'
      );
      checkedBrandCheckboxes.forEach((checkbox) => {
        updateHorizontalFormInput(checkbox.name, checkbox.value);
      });

      // Copy sort order
      const sortByInput = mobileForm.querySelector('select[name="sort_by"]');
      if (sortByInput) {
        updateHorizontalFormInput("sort_by", sortByInput.value);
      }
    }
  }

  // Helper function to update or add input to the horizontal form
  function updateHorizontalFormInput(name, value) {
    const horizontalForm = document.getElementById("HorizontalFiltersForm");
    if (!horizontalForm) return;

    let input = horizontalForm.querySelector(
      `input[name="${name}"][value="${value}"]`
    );

    // For non-checkbox inputs like price and sort
    if (
      name === "filter.v.price.gte" ||
      name === "filter.v.price.lte" ||
      name === "sort_by"
    ) {
      input = horizontalForm.querySelector(`input[name="${name}"]`);
    }

    if (!input) {
      input = document.createElement("input");
      input.type = "hidden";
      input.name = name;
      horizontalForm.appendChild(input);
    }

    input.value = value;
  }

  // Helper function to remove input from horizontal form
  function removeHorizontalFormInput(name, value) {
    const horizontalForm = document.getElementById("HorizontalFiltersForm");
    if (!horizontalForm) return;

    if (value) {
      // For checkboxes with name and value
      const input = horizontalForm.querySelector(
        `input[name="${name}"][value="${value}"]`
      );
      if (input) input.remove();
    } else {
      // For simple inputs like price
      const input = horizontalForm.querySelector(`input[name="${name}"]`);
      if (input) input.remove();
    }
  }

  // Function to submit the horizontal filter form
  function submitHorizontalFilterForm() {
    const form = document.getElementById("HorizontalFiltersForm");
    if (form) {
      // Copy any other necessary inputs from mobile form
      const mobileForm = document.getElementById("FacetFiltersFormMobile");
      const sortByInput = mobileForm.querySelector('select[name="sort_by"]');
      if (sortByInput) {
        updateHorizontalFormInput("sort_by", sortByInput.value);
      }

      // Build the search params and trigger page render
      const formData = new FormData(form);
      const searchParams = new URLSearchParams(formData).toString();
      FacetFiltersForm.renderPage(searchParams, null);
    }
  }

  // Ensure the horizontal filters stay in sync with drawer filters when page is updated
  document.addEventListener("facetFilterFormUpdated", () => {
    // Re-sync the entire form to make sure state is consistent
    syncFormFromMobile();

    // Update visible UI elements to match the form state

    // Sync price filter values
    const drawerPriceMin = document.querySelector("#Mobile-Filter-Price-GTE");
    const drawerPriceMax = document.querySelector("#Mobile-Filter-Price-LTE");
    const horizontalPriceMin = document.querySelector(
      "#Horizontal-Filter-Price-GTE"
    );
    const horizontalPriceMax = document.querySelector(
      "#Horizontal-Filter-Price-LTE"
    );

    if (drawerPriceMin && horizontalPriceMin) {
      horizontalPriceMin.value = drawerPriceMin.value;
    }

    if (drawerPriceMax && horizontalPriceMax) {
      horizontalPriceMax.value = drawerPriceMax.value;
    }

    // Sync brand/vendor checkboxes
    const horizontalBrandCheckboxes = document.querySelectorAll(
      '.horizontal-filter[data-filter-type="brand"] input[type="checkbox"]'
    );
    horizontalBrandCheckboxes.forEach((checkbox) => {
      const value = checkbox.getAttribute("data-value");
      const name = checkbox.getAttribute("name");
      const drawerCheckbox = document.querySelector(
        `#FacetFiltersFormMobile input[name="${name}"][value="${value}"]`
      );

      if (drawerCheckbox) {
        checkbox.checked = drawerCheckbox.checked;
      }
    });

    // Update the "filters selected" counters in horizontal filters
    updateFilterCounters();
  });

  // Function to update filter counters in horizontal filters
  function updateFilterCounters() {
    // Update brand filter counter
    const brandFilter = document.querySelector(
      '.horizontal-filter[data-filter-type="brand"]'
    );
    if (brandFilter) {
      const checkedBrandInputs = brandFilter.querySelectorAll(
        'input[type="checkbox"]:checked'
      );
      const brandCountElement = brandFilter.querySelector(".facets__selected");

      if (brandCountElement) {
        const count = checkedBrandInputs.length;
        // Use the same translation as in the liquid template
        brandCountElement.textContent =
          window.theme && window.theme.strings
            ? window.theme.strings.products_filters_selected.replace(
                "[count]",
                count
              )
            : count + " selected";

        // Also update any parent elements that might be hiding this element if count is 0
        if (count > 0) {
          brandCountElement.classList.remove("hidden");
          const parentElement = brandCountElement.closest(".facets__header");
          if (parentElement) parentElement.classList.remove("hidden");
        }
      }
    }

    // Update price filter counter if it shows selections
    const priceFilter = document.querySelector(
      '.horizontal-filter[data-filter-type="price"]'
    );
    if (priceFilter) {
      const minInput = document.querySelector("#Horizontal-Filter-Price-GTE");
      const maxInput = document.querySelector("#Horizontal-Filter-Price-LTE");

      // Check if price filter has any active values
      const hasActiveValues =
        (minInput && minInput.value) || (maxInput && maxInput.value);

      // If we need to update any counters for price filter, do it here
      // This depends on the specific UI, but might not be needed if price doesn't show a counter
    }
  }
}

// Make sure to call this when the horizontal filter checkboxes are changed directly
const horizontalBrandCheckboxes = document.querySelectorAll(
  '.horizontal-filter[data-filter-type="brand"] input[type="checkbox"]'
);
if (horizontalBrandCheckboxes.length) {
  horizontalBrandCheckboxes.forEach((checkbox) => {
    checkbox.addEventListener("change", function () {
      updateFilterCounters();
    });
  });
}

// Specifically handle facet pill removal to update horizontal checkboxes
document.addEventListener("click", function (event) {
  // Check if a facet-remove pill was clicked
  const facetRemove = event.target.closest("facet-remove a");
  if (facetRemove) {
    // After the page is rerendered, we need to make sure horizontal checkboxes reflect the changes
    setTimeout(() => {
      // This ensures our code runs after the facet-remove's closeFilter completes
      syncHorizontalBrandCheckboxes();
    }, 100);
  }
});

// Function to sync horizontal brand checkboxes with drawer checkboxes
function syncHorizontalBrandCheckboxes() {
  const horizontalBrandCheckboxes = document.querySelectorAll(
    '.horizontal-filter[data-filter-type="brand"] input[type="checkbox"]'
  );

  if (horizontalBrandCheckboxes.length) {
    // First, uncheck all horizontal checkboxes
    horizontalBrandCheckboxes.forEach((checkbox) => {
      checkbox.checked = false;
    });

    // Then, only check the ones that are checked in the drawer
    const drawerCheckedCheckboxes = document.querySelectorAll(
      '#FacetFiltersFormMobile input[type="checkbox"]:checked'
    );

    drawerCheckedCheckboxes.forEach((drawerCheckbox) => {
      const name = drawerCheckbox.getAttribute("name");
      const value = drawerCheckbox.getAttribute("value");

      // Find the corresponding horizontal checkbox
      const horizontalCheckbox = document.querySelector(
        `.horizontal-filter[data-filter-type="brand"] input[name="${name}"][data-value="${value}"]`
      );

      if (horizontalCheckbox) {
        horizontalCheckbox.checked = true;
      }
    });

    // Directly update the brand filter counter with the current checked state
    updateBrandFilterCounter();

    // Log the sync operation for debugging
    console.log("Horizontal checkboxes synced with drawer checkboxes");
  }
}

// Function specifically for updating the brand filter counter
function updateBrandFilterCounter() {
  const brandFilter = document.querySelector(
    '.horizontal-filter[data-filter-type="brand"]'
  );
  if (brandFilter) {
    const checkedBrandInputs = brandFilter.querySelectorAll(
      'input[type="checkbox"]:checked'
    );
    const brandCountElement = brandFilter.querySelector(".facets__selected");

    if (brandCountElement) {
      const count = checkedBrandInputs.length;

      // Use the same translation as in the liquid template or fallback to simple text
      const countText = count + " selected";
      brandCountElement.textContent = countText;

      // Make sure the counter is visible
      brandCountElement.classList.remove("hidden");

      // Update any parent elements that need to be visible when there are selections
      const parentElement = brandCountElement.closest(".facets__header");
      if (parentElement) {
        parentElement.classList.remove("hidden");
      }

      // Log for debugging
      console.log("Brand filter counter updated:", count, brandCountElement);
    }
  }
}

// Improved handler for facet pill removal
function handleFacetPillRemoval(e) {
  // Capture a snapshot of the current URL
  const currentUrl = window.location.href;

  // Add event listeners to detect when the page has finished updating
  const checkForUpdates = setInterval(() => {
    // If URL has changed, the filter update has been applied
    if (window.location.href !== currentUrl) {
      clearInterval(checkForUpdates);

      // After the page updates, apply our horizontal filter sync
      setTimeout(() => {
        syncHorizontalBrandCheckboxes();
        console.log("Horizontal filters synced after URL change");
      }, 50);
    }
  }, 10);

  // Set a timeout to clear the interval if it doesn't trigger within 2 seconds
  setTimeout(() => {
    clearInterval(checkForUpdates);
  }, 2000);
}

// Replace the click event listener with our improved handler
document.removeEventListener("click", function (event) {
  const facetRemove = event.target.closest("facet-remove a");
  if (facetRemove) {
    setTimeout(() => {
      syncHorizontalBrandCheckboxes();
    }, 100);
  }
});

document.addEventListener("click", function (event) {
  const facetRemove = event.target.closest("facet-remove a");
  if (facetRemove) {
    handleFacetPillRemoval(event);
  }
});

// Monitor URL changes to detect when filters are modified
let lastUrl = window.location.href;

// Create a MutationObserver to detect DOM changes in the active facets
const facetsObserver = new MutationObserver(function (mutations) {
  // Check for changes to the active facets
  const hasFacetChanges = mutations.some((mutation) => {
    const target = mutation.target;
    return (
      target.classList &&
      (target.classList.contains("active-facets-desktop") ||
        target.classList.contains("active-facets-mobile"))
    );
  });

  if (hasFacetChanges) {
    console.log("Active facets changed, syncing horizontal filters...");
    // Force a synchronization of the horizontal filters
    setTimeout(() => {
      forceHorizontalFilterSync();
    }, 50);
  }
});

// Start observing the active facets sections
document.addEventListener("DOMContentLoaded", () => {
  const activeDesktopFacets = document.querySelector(".active-facets-desktop");
  const activeMobileFacets = document.querySelector(".active-facets-mobile");

  if (activeDesktopFacets) {
    facetsObserver.observe(activeDesktopFacets, {
      childList: true,
      subtree: true,
    });
  }
  if (activeMobileFacets) {
    facetsObserver.observe(activeMobileFacets, {
      childList: true,
      subtree: true,
    });
  }
});

// Improved function to force synchronization of horizontal filters with mobile filters
function forceHorizontalFilterSync() {
  // Directly check the mobile form for checked checkboxes
  const mobileForm = document.getElementById("FacetFiltersFormMobile");
  if (!mobileForm) return;

  // Get all checked checkboxes in the mobile form
  const checkedMobileCheckboxes = mobileForm.querySelectorAll(
    'input[type="checkbox"]:checked'
  );
  const checkedValues = Array.from(checkedMobileCheckboxes).map((checkbox) => {
    return {
      name: checkbox.getAttribute("name"),
      value: checkbox.getAttribute("value"),
    };
  });

  console.log("Mobile checked values:", checkedValues);

  // Reset all horizontal checkboxes first
  const horizontalCheckboxes = document.querySelectorAll(
    '.horizontal-filter[data-filter-type="brand"] input[type="checkbox"]'
  );

  horizontalCheckboxes.forEach((checkbox) => {
    checkbox.checked = false;
  });

  // Now check only the ones that are checked in the mobile form
  horizontalCheckboxes.forEach((checkbox) => {
    const name = checkbox.getAttribute("name");
    const value = checkbox.getAttribute("data-value");

    const isChecked = checkedValues.some(
      (item) => item.name === name && item.value === value
    );

    checkbox.checked = isChecked;
  });

  // Update the counter display - directly update the count element
  const brandFilter = document.querySelector(
    '.horizontal-filter[data-filter-type="brand"]'
  );
  if (brandFilter) {
    const brandCountElement = brandFilter.querySelector(".facets__selected");

    if (brandCountElement) {
      // Filter by name pattern for vendor/brand
      const vendorCheckboxes = checkedValues.filter((item) =>
        item.name.includes("filter.p.vendor")
      );
      const count = vendorCheckboxes.length;

      // Update the text content with the count
      const countText = count + " selected";
      brandCountElement.textContent = countText;

      // Make sure the counter is visible
      brandCountElement.classList.remove("hidden");

      // Update any parent elements that need to be visible when there are selections
      const parentElement = brandCountElement.closest(".facets__header");
      if (parentElement) {
        parentElement.classList.remove("hidden");
      }

      console.log("Updated brand filter counter to:", count);
    }
  }

  console.log("Horizontal filters synchronized");
}
